/* eslint-disable max-lines-per-function, max-statements */
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';

import { getClientTimeout } from '../services/clientTimeoutConfig';
import { isRetryableError as defaultIsRetryable } from '../utils/errorClassification';

export type AutoReconnectState =
  | { phase: 'idle' }
  | { phase: 'counting'; secondsLeft: number; attempt: number; maxAttempts: number }
  | { phase: 'retrying'; attempt: number; maxAttempts: number }
  | { phase: 'reconnected' }
  | { phase: 'gaveUp'; attempts: number };

interface UseAutoReconnectOptions {
  error: string | null;
  onRetry: () => Promise<unknown>;
  /** Changes when retry work belongs to a different project/session. */
  scopeKey?: string | null;
  isRetryable?: (e: string) => boolean;
  /** Automatic retries before giving up; defaults to the panel's `client.project_reconnect_attempts`. */
  maxAttempts?: number;
  /** Countdown before each automatic retry; defaults to the panel's `client.project_reconnect_countdown_s`. */
  countdownSeconds?: number;
  successFlashMs?: number;
  enabled?: boolean;
}

const IDLE: AutoReconnectState = { phase: 'idle' };

type Action = { kind: 'set'; state: AutoReconnectState };

const reducer = (_prev: AutoReconnectState, action: Action): AutoReconnectState => action.state;

interface Engine {
  setState: (s: AutoReconnectState) => void;
  schedule: (fn: () => void, ms: number) => void;
  cancelScheduled: () => void;
  invalidate: () => void;
  isMounted: () => boolean;
  currentToken: () => RetryToken;
  isCurrent: (token: RetryToken) => boolean;
  config: {
    onRetry: () => Promise<unknown>;
    scopeKey: string | null;
    isRetryable: (e: string) => boolean;
    state: AutoReconnectState;
    maxAttempts: number;
    countdownSeconds: number;
    successFlashMs: number;
  };
}

interface RetryToken {
  generation: number;
  scopeKey: string | null;
}

const startCountdownAt = (engine: Engine, attempt: number) => {
  // A budget of zero automatic retries leaves only the manual "Try again".
  if (attempt > engine.config.maxAttempts) {
    engine.setState({ phase: 'gaveUp', attempts: attempt - 1 });

    return;
  }
  engine.setState({
    phase: 'counting',
    secondsLeft: engine.config.countdownSeconds,
    attempt,
    maxAttempts: engine.config.maxAttempts,
  });
};

const finishSuccess = (engine: Engine, token: RetryToken) => {
  if (!engine.isCurrent(token)) return;
  engine.setState({ phase: 'reconnected' });
  engine.schedule(() => {
    if (engine.isCurrent(token)) engine.setState(IDLE);
  }, engine.config.successFlashMs);
};

const finishFailure = (engine: Engine, attempt: number) => {
  if (attempt >= engine.config.maxAttempts) {
    engine.setState({ phase: 'gaveUp', attempts: attempt });
  } else {
    startCountdownAt(engine, attempt + 1);
  }
};

const runRetry = async (engine: Engine, attempt: number) => {
  const token = engine.currentToken();

  engine.cancelScheduled();
  engine.setState({ phase: 'retrying', attempt, maxAttempts: engine.config.maxAttempts });
  try {
    await engine.config.onRetry();
    if (engine.isCurrent(token)) finishSuccess(engine, token);
  } catch {
    if (engine.isCurrent(token)) finishFailure(engine, attempt);
  }
};

const tickCountdown = (engine: Engine, secondsLeft: number, attempt: number) => {
  if (secondsLeft > 1) {
    engine.setState({
      phase: 'counting',
      secondsLeft: secondsLeft - 1,
      attempt,
      maxAttempts: engine.config.maxAttempts,
    });
  } else {
    runRetry(engine, attempt);
  }
};

const shouldIgnoreErrorChange = (current: AutoReconnectState, error: string | null) => {
  if (error !== null) return false;

  return current.phase === 'retrying' || current.phase === 'reconnected';
};

const isAlreadyHandling = (current: AutoReconnectState) => {
  const { phase } = current;

  return phase === 'counting'
    || phase === 'retrying'
    || phase === 'reconnected'
    || phase === 'gaveUp';
};

const handleErrorChange = (engine: Engine, error: string | null) => {
  const current = engine.config.state;

  if (shouldIgnoreErrorChange(current, error)) return;
  if (!error || !engine.config.isRetryable(error)) {
    engine.cancelScheduled();
    if (error && current.phase === 'retrying') engine.invalidate();
    engine.setState(IDLE);

    return;
  }
  if (isAlreadyHandling(current)) return;
  startCountdownAt(engine, 1);
};

const computeRetryAttempt = (
  current: AutoReconnectState,
): number => current.phase === 'counting' ? current.attempt : 1;

export function useAutoReconnect(opts: UseAutoReconnectOptions): {
  state: AutoReconnectState;
  retryNow: () => void;
} {
  const {
    error, onRetry,
    scopeKey = null,
    isRetryable = defaultIsRetryable,
    maxAttempts = getClientTimeout('projectReconnectAttempts'),
    countdownSeconds = getClientTimeout('projectReconnectCountdownSeconds'),
    successFlashMs = 1500,
    enabled = true,
  } = opts;

  const [state, dispatch] = useReducer(reducer, IDLE);
  const setState = useCallback(
    (s: AutoReconnectState) => dispatch({ kind: 'set', state: s }),
    [],
  );

  const configRef = useRef<Engine['config']>({
    onRetry, scopeKey, isRetryable, state, maxAttempts, countdownSeconds, successFlashMs,
  });

  // Layout effects run before the passive effects below, so they always read this render's options.
  useLayoutEffect(() => {
    configRef.current = {
      onRetry, scopeKey, isRetryable, state, maxAttempts, countdownSeconds, successFlashMs,
    };
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const previousScopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    // React StrictMode runs setup -> cleanup -> setup in development.
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, []);

  const engine = useMemo<Engine>(() => ({
    setState: (s) => { if (mountedRef.current) setState(s); },
    schedule: (fn, ms) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fn();
      }, ms);
    },
    cancelScheduled: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    },
    invalidate: () => { generationRef.current += 1; },
    isMounted: () => mountedRef.current,
    currentToken: () => ({
      generation: generationRef.current,
      scopeKey: configRef.current.scopeKey,
    }),
    isCurrent: (token) => (
      mountedRef.current
      && token.generation === generationRef.current
      && token.scopeKey === configRef.current.scopeKey
    ),
    get config() { return configRef.current; },
  }), [setState]);

  useEffect(() => {
    const scopeChanged = previousScopeKeyRef.current !== scopeKey;

    if (scopeChanged) {
      previousScopeKeyRef.current = scopeKey;
      engine.invalidate();
      engine.cancelScheduled();
      if (enabled && error && isRetryable(error)) startCountdownAt(engine, 1);
      else engine.setState(IDLE);

      return;
    }
    if (!enabled) {
      engine.invalidate();
      engine.cancelScheduled();
      engine.setState(IDLE);

      return;
    }
    handleErrorChange(engine, error);
  }, [error, enabled, engine, isRetryable, scopeKey]);

  useEffect(() => {
    if (
      state.phase !== 'counting'
      || !enabled
      || !error
      || !isRetryable(error)
    ) return;
    const token = engine.currentToken();

    // A zero-second countdown retries at once instead of after one tick.
    engine.schedule(() => {
      if (engine.isCurrent(token)) tickCountdown(engine, state.secondsLeft, state.attempt);
    }, state.secondsLeft > 0 ? 1000 : 0);
  }, [enabled, engine, error, isRetryable, state]);

  const retryNow = useCallback(() => {
    if (!enabled) return;
    const current = configRef.current.state;

    if (current.phase === 'retrying') return;
    runRetry(engine, computeRetryAttempt(current));
  }, [enabled, engine]);

  return { state, retryNow };
}
